import { useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { AlertDialogCancel } from '@radix-ui/react-alert-dialog'
import { FormProvider, useForm, type UseFormReturn } from 'react-hook-form'
import { withMask } from 'use-mask-input'
import type z from 'zod'

import { createDialog, type DialogComponentProps } from '@/components/create-dialog'
import { Icon } from '@/components/icon'
import { Button, InputField } from '@/components/ui'
import {
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialog as AlertDialogPrimitive,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  DrawerFooter,
  DrawerClose,
} from '@/components/ui/drawer'
import { useIsMobile } from '@/hooks'
import { cn } from '@/infra'
import { CPF_MASK } from '@/utils/masks'

interface DialogIncluirCpfClienteOptions {
  titulo: string
  nome?: string
  telefone?: string
  labels: {
    cpf: string
    placeholder: string
    botaoConfirmar: string
    botaoCancelar: string
  }
  schema: z.ZodType<FormData, FormData>
  containerClass?: string
}

interface FormData {
  cpf: string
}

interface CommonFormProps {
  form: UseFormReturn<FormData>
  onSalvarCpf: (data: FormData) => void
  onCancel: () => void
  options: DialogIncluirCpfClienteOptions
}

const CpfFormFields = ({
  form,
  options,
}: {
  form: CommonFormProps['form']
  options: DialogIncluirCpfClienteOptions
}) => (
  <div className="max-w-full space-y-2 px-6 py-4">
    <InputField
      ref={withMask('cpf', CPF_MASK)}
      id="cpf"
      name="cpf"
      control={form.control}
      type="text"
      label={options.labels.cpf}
      placeholder={options.labels.placeholder}
    />
  </div>
)

const MobileDialog = ({
  form,
  onSalvarCpf,
  onCancel,
  open,
  onOpenChange,
  options,
}: CommonFormProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
}) => (
  <Drawer open={open} onOpenChange={onOpenChange} onClose={onCancel} direction="bottom">
    <DrawerContent className="gap-0 p-0">
      <FormProvider {...form}>
        <DrawerHeader className="relative pb-2 text-left">
          <DrawerClose className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none">
            <Icon name="close" className="h-4 w-4" size={24} />
          </DrawerClose>
          <DrawerTitle className="pt-8 text-left text-lg font-medium text-gray-900">{options.titulo}</DrawerTitle>
        </DrawerHeader>
        <DrawerDescription className="font-nunito text-md border-muted m-0 flex flex-col border-t border-b px-6 py-4 text-neutral-700">
          {options.nome && <span className="capitalize">{options.nome}</span>}
          {options.telefone && <span>{options.telefone}</span>}
        </DrawerDescription>
        <div className="space-y-6">
          <CpfFormFields form={form} options={options} />
        </div>
        <DrawerFooter className="px-6 pt-0 pb-6">
          <Button type="submit" className="w-full" onClick={form.handleSubmit(onSalvarCpf)}>
            {options.labels.botaoConfirmar}
          </Button>
          <DrawerClose asChild>
            <Button type="button" variant="outline" className="w-full" onClick={onCancel}>
              {options.labels.botaoCancelar}
            </Button>
          </DrawerClose>
        </DrawerFooter>
      </FormProvider>
    </DrawerContent>
  </Drawer>
)

const DesktopDialog = ({
  form,
  onSalvarCpf,
  onCancel,
  open,
  onOpenChange,
  options,
}: CommonFormProps & {
  open: boolean
  onOpenChange: (open: boolean) => void
}) => (
  <AlertDialogPrimitive open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent className={cn('gap-0 p-0 sm:max-w-109', options.containerClass)}>
      <FormProvider {...form}>
        <AlertDialogHeader className="relative py-6 pr-14 pl-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="absolute top-6 right-6 h-6 w-6 p-0 opacity-70 hover:opacity-100"
          >
            <Icon name="close" size={24} />
          </Button>
          <AlertDialogTitle className="text-xl font-medium">{options.titulo}</AlertDialogTitle>
        </AlertDialogHeader>
        {options && (
          <AlertDialogDescription className="font-nunito text-md border-muted m-0 flex flex-col border-t border-b px-6 py-4 text-neutral-700">
            {options.nome && <span className="capitalize">{options.nome}</span>}
            {options.telefone && <span>{options.telefone}</span>}
          </AlertDialogDescription>
        )}
        <div className="space-y-6">
          <CpfFormFields form={form} options={options} />
        </div>
        <AlertDialogFooter className="px-6 pb-6 sm:justify-end">
          <AlertDialogCancel asChild onClick={onCancel}>
            <Button type="button" variant="outline">
              {options.labels.botaoCancelar}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction onClick={form.handleSubmit(onSalvarCpf)}>
            {options.labels.botaoConfirmar}
          </AlertDialogAction>
        </AlertDialogFooter>
      </FormProvider>
    </AlertDialogContent>
  </AlertDialogPrimitive>
)

const DialogIncluirCpfClienteGenerico = ({
  onAction,
  options,
}: DialogComponentProps<DialogIncluirCpfClienteOptions, { cpf?: string }>) => {
  const form = useForm<FormData>({
    mode: 'onSubmit',
    defaultValues: { cpf: '' },
    resolver: zodResolver(options.schema),
  })
  const [open, setOpen] = useState(true)
  const isMobile = useIsMobile()

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) onAction('dismiss')
  }

  const onSalvarCpf = (data: FormData) => {
    onAction('confirm', { cpf: data.cpf })
    setOpen(false)
  }
  const onCancel = () => {
    onAction('cancel')
    setOpen(false)
  }

  const commonProps = {
    form,
    onSalvarCpf,
    onCancel,
    open,
    onOpenChange: handleOpenChange,
    options,
  }

  return isMobile ? <MobileDialog {...commonProps} /> : <DesktopDialog {...commonProps} />
}

export const DialogIncluirCpfCliente = createDialog(DialogIncluirCpfClienteGenerico)
